import { Link } from '@tanstack/react-router';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';

import Brand from '../../common/Brand';
import LanguageSwitcher from '../../common/LanguageSwitcher';
import type { FooterColumn, FooterLink, FooterProps } from './useFooter';

const FooterLink = ({
  link,
  column,
  handleServiceClick,
}: {
  link: FooterLink;
  column: FooterColumn;
  handleServiceClick: FooterProps['handleServiceClick'];
}) => {
  if (link.disabled) {
    return (
      <button
        type="button"
        onClick={(e) => handleServiceClick(e, link, column.id)}
        className="cursor-pointer text-left font-body text-sm transition-colors hover:text-white/90"
        style={{ color: 'rgba(255, 255, 255, 0.7)' }}
      >
        {link.label}
      </button>
    );
  }

  if (column.id === 'services') {
    return (
      <button
        type="button"
        onClick={(e) => handleServiceClick(e, link, column.id)}
        className="cursor-pointer text-left font-body text-sm transition-colors hover:text-white/90"
        style={{ color: 'rgba(255, 255, 255, 0.7)' }}
      >
        {link.label}
      </button>
    );
  }

  return (
    <Link
      to={link.href}
      className="font-body text-sm transition-colors hover:text-white/90"
      style={{ color: 'rgba(255, 255, 255, 0.7)' }}
    >
      {link.label}
    </Link>
  );
};

const FooterMobile = ({ data, handleServiceClick }: FooterProps) => {
  return (
    <footer className="bg-surface-inverse px-6 pb-8 pt-12">
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-6">
          <Brand link="/" variant="light" />
          <p
            className="font-body text-sm leading-[1.6]"
            style={{ color: 'rgba(255, 255, 255, 0.6)' }}
          >
            {data.tagline}
          </p>
          <LanguageSwitcher variant="light" className="w-fit" />
        </div>

        <div className="grid grid-cols-2 gap-8">
          {data.columns.slice(0, 2).map((column) => (
            <div key={column.title} className="flex flex-col gap-4">
              <h3 className="font-body text-sm font-semibold text-foreground-inverse">
                {column.title}
              </h3>
              <div className="flex flex-col gap-3">
                {column.links.map((link) => (
                  <FooterLink
                    key={link.id}
                    link={link}
                    column={column}
                    handleServiceClick={handleServiceClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {data.columns[2] && (
          <div className="flex flex-col gap-4">
            <h3 className="font-body text-sm font-semibold text-foreground-inverse">
              {data.columns[2].title}
            </h3>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {data.columns[2].links.map((link) => (
                <FooterLink
                  key={link.id}
                  link={link}
                  column={data.columns[2]}
                  handleServiceClick={handleServiceClick}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className="h-px w-full"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.125)' }}
        />

        <div className="flex flex-col items-center gap-6">
          <div className="flex gap-6">
            <button
              type="button"
              className="transition-colors hover:text-white/80"
              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
              aria-label="Facebook"
            >
              <Facebook size={20} />
            </button>
            <button
              type="button"
              className="transition-colors hover:text-white/80"
              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
              aria-label="Twitter"
            >
              <Twitter size={20} />
            </button>
            <button
              type="button"
              className="transition-colors hover:text-white/80"
              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
              aria-label="Instagram"
            >
              <Instagram size={20} />
            </button>
            <button
              type="button"
              className="transition-colors hover:text-white/80"
              style={{ color: 'rgba(255, 255, 255, 0.5)' }}
              aria-label="LinkedIn"
            >
              <Linkedin size={20} />
            </button>
          </div>
          <p
            className="text-center font-body text-xs"
            style={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {data.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default FooterMobile;
